import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, InputBase, Typography } from '@mui/material';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppLoader from '../components/AppLoader';
import * as Colors from '../theme/colors';
import {
  ContractorAgreementDocument,
  acceptAgreement,
  fetchAgreement,
} from '../services/contractors';

const CARD_WIDTH = 762;
const CARD_HEIGHT = 900;
const SUCCESS_CARD_HEIGHT = 518;
const GUTTER = 32;
const TERMS_HEIGHT = 366;

const BLUE_TINT = 'rgba(37, 99, 235, 0.12)';
const BLUE_TINT_BORDER = 'rgba(37, 99, 235, 0.23)';
const GREEN_TINT = 'rgba(23, 178, 106, 0.12)';
const GREEN_TINT_BORDER = 'rgba(23, 178, 106, 0.34)';

const cardSx = {
  width: CARD_WIDTH,
  maxWidth: 'calc(100vw - 32px)',
  bgcolor: Colors.Black,
  border: `1px solid ${Colors.ModalStroke}`,
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
} as const;

const pageSx = {
  minHeight: '100vh',
  bgcolor: Colors.Black,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 4,
} as const;

const Brand: React.FC = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
    <Box
      sx={{
        width: 45,
        height: 45,
        flexShrink: 0,
        borderRadius: '8px',
        bgcolor: BLUE_TINT,
        border: `1px solid ${BLUE_TINT_BORDER}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box component="img" src="/zerac-icon.svg" alt="" sx={{ width: 27, height: 27 }} />
    </Box>
    <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: Colors.White }}>
      Zerac 3rd Party Contractor
    </Typography>
  </Box>
);

const AgreementSuccess: React.FC<{ onContinue: () => void }> = ({ onContinue }) => (
  <Box sx={pageSx}>
    <Box
      sx={{
        ...cardSx,
        height: SUCCESS_CARD_HEIGHT,
        maxHeight: 'calc(100vh - 64px)',
        alignItems: 'center',
        justifyContent: 'center',
        px: `${GUTTER}px`,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '8px',
          bgcolor: GREEN_TINT,
          border: `1px solid ${GREEN_TINT_BORDER}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 28, color: Colors.Green }} />
      </Box>

      <Typography
        sx={{
          mt: '24px',
          fontSize: 20,
          lineHeight: '28px',
          fontWeight: 600,
          color: Colors.White,
          textAlign: 'center',
        }}
      >
        You&apos;re all set!
      </Typography>

      <Typography
        sx={{
          mt: '16px',
          maxWidth: 590,
          fontSize: 14,
          lineHeight: '20px',
          fontWeight: 500,
          color: Colors.LightGray,
          textAlign: 'center',
        }}
      >
        Your signature is recorded against your account. You can now reach the machines the
        organizations you work for have granted you.
      </Typography>

      <Button
        variant="contained"
        disableElevation
        onClick={onContinue}
        sx={{ mt: '24px', width: 169, height: 40, borderRadius: '8px' }}
      >
        Go to Dashboard
      </Button>
    </Box>
  </Box>
);

type Props = {
  onSigned: () => void;
};

const ContractorAgreement: React.FC<Props> = ({ onSigned }) => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const [document, setDocument] = useState<ContractorAgreementDocument | null>(null);
  const [signature, setSignature] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAccessTokenSilently()
      .then((token) => fetchAgreement(token))
      .then((response) => {
        if (cancelled) return;
        setDocument(response.document);
        if (response.status.acceptedAt) setSigned(true);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [getAccessTokenSilently]);

  const sign = useCallback(async () => {
    if (!document || signature.trim().length < 2) return;
    setSigning(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      await acceptAgreement(token, document.version, signature.trim());
      setSigned(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSigning(false);
    }
  }, [document, signature, getAccessTokenSilently]);

  if (signed) return <AgreementSuccess onContinue={onSigned} />;
  if (!document) return <AppLoader />;

  return (
    <Box sx={pageSx}>
      <Box sx={{ ...cardSx, height: CARD_HEIGHT, maxHeight: 'calc(100vh - 64px)' }}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            pt: `${GUTTER}px`,
            px: `${GUTTER}px`,
          }}
        >
          <Brand />

          <Typography
            sx={{
              mt: '32px',
              fontSize: 24,
              lineHeight: '32px',
              fontWeight: 600,
              color: Colors.White,
              textAlign: 'center',
            }}
          >
            {document.title}
          </Typography>

          <Typography
            sx={{
              mt: '12px',
              fontSize: 16,
              lineHeight: '24px',
              color: Colors.LightGray,
              textAlign: 'center',
            }}
          >
            {document.subtitle}
          </Typography>

          <Box
            sx={{
              mt: '48px',
              height: TERMS_HEIGHT,
              flexShrink: 1,
              minHeight: 140,
              overflowY: 'auto',
              bgcolor: Colors.PanelBackground,
              border: `1px solid ${Colors.ModalStroke}`,
              borderRadius: '8px',
              py: `${GUTTER}px`,
              px: '24px',
              '&::-webkit-scrollbar': { width: 18 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: Colors.ModalStroke,
                borderRadius: 999,
                border: '6px solid transparent',
                backgroundClip: 'content-box',
              },
            }}
          >
            {document.sections.map((section, index) => (
              <Box key={section.heading} sx={{ mt: index === 0 ? 0 : '20px' }}>
                <Typography
                  sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: Colors.White }}
                >
                  {section.heading}
                </Typography>
                {section.paragraphs.map((paragraph) => (
                  <Typography
                    key={paragraph}
                    sx={{ mt: '16px', fontSize: 14, lineHeight: '20px', color: Colors.LightGray }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>

          <Typography
            sx={{ mt: '16px', fontSize: 12, lineHeight: '16px', color: Colors.White }}
          >
            {document.signatureStatement}
          </Typography>

          <Typography
            sx={{ mt: '24px', fontSize: 12, lineHeight: '16px', color: Colors.InputLabel }}
          >
            Type your full legal name to sign
          </Typography>

          <InputBase
            value={signature}
            onChange={(event) => setSignature(event.target.value)}
            inputProps={{ 'aria-label': 'Type your full legal name to sign', maxLength: 200 }}
            sx={{
              mt: '8px',
              height: 48,
              px: '14px',
              borderRadius: '8px',
              bgcolor: Colors.InputBackground,
              border: `1px solid ${Colors.BorderGray}`,
              color: Colors.White,
              fontSize: 14,
              '&.Mui-focused': { borderColor: Colors.Blue },
            }}
          />

          <Typography sx={{ fontSize: 12, lineHeight: '16px', color: Colors.HelperText }}>
            Timestamped and stored in your Documents section
          </Typography>

          {error && (
            <Typography sx={{ mt: '12px', fontSize: 12, lineHeight: '16px', color: Colors.Red }}>
              {error}
            </Typography>
          )}

          <Box sx={{ flexGrow: 1 }} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            px: `${GUTTER}px`,
            pt: '24px',
            pb: `${GUTTER}px`,
            borderTop: `1px solid ${Colors.ModalStroke}`,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            sx={{
              width: 248,
              height: 40,
              borderRadius: '8px',
              borderColor: Colors.OutlineButtonBorder,
              color: Colors.OutlineButtonText,
            }}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => void sign()}
            disabled={signing || signature.trim().length < 2}
            endIcon={<DrawOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{ flex: 1, height: 40, borderRadius: '8px' }}
          >
            {signing ? 'Signing' : 'Sign & continue to Dashboard'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ContractorAgreement;
